import { sampleQueryKeys } from '@/config/queryKeys';
import { useDateFormatter } from '@/hooks';
import { useApiClient } from '@/hooks/useApiClient';
import type { PaginatedSampleProteoformsResponse, SampleProteoformPublic, SampleWithNamesPublic } from '@/types';
import { FullLoading } from '@components/Loaders';
import Accordion from '@nautilusbio/nb-design-system/Accordion';
import AccordionGroup from '@nautilusbio/nb-design-system/AccordionGroup';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack, type StackProps } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ColDef } from 'ag-grid-enterprise';
import clsx from 'clsx';
import React, { memo, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import pageStyles from '../../../../styles/pages/index.module.css';

import { PROTEOFORM_SEARCH_BODY } from './constants';
import styles from './styles.module.css';

interface SampleDetailsPageProps {
  sample: SampleWithNamesPublic;
  orgId: string;
}

const sharedStackProps: Partial<StackProps> = {
  alignItems: 'center',
  className: styles.fieldRow,
  fullWidth: true,
  gap: 4,
  orientation: 'horizontal',
};

function SampleDetailsPageComponent({ sample, orgId }: SampleDetailsPageProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const api = useApiClient();

  const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(new Set(['details', 'proteoforms']));

  const {
    data: proteoformData,
    isLoading: proteoformsLoading,
    isError: proteoformsError,
  } = useQuery({
    queryKey: sampleQueryKeys.proteoformsSearch(orgId, sample.id),
    queryFn: async () => {
      const url = sampleQueryKeys.endpoints.proteoformsSearch(orgId, sample.id);
      const response = await api.post<PaginatedSampleProteoformsResponse>(url, PROTEOFORM_SEARCH_BODY);
      if (!response) throw new Error('No response from server');
      return response;
    },
    enabled: !!orgId && !!sample.id,
    retry: 1,
  });

  const columnDefs = useMemo<ColDef<SampleProteoformPublic>[]>(
    () => [
      {
        headerName: t('columnHeaders.proteoform'),
        field: 'proteoform_name',
        flex: 1,
      },
      {
        headerName: t('columnHeaders.abundance'),
        field: 'proteoform_abundance',
        valueFormatter: ({ value }: { value: number }) =>
          value !== undefined && value !== null ? value.toLocaleString() : '',
      },
      { headerName: t('columnHeaders.wellPosition'), field: 'well_position' },
      {
        headerName: t('columnHeaders.createdAt'),
        field: 'created_at',
        valueFormatter: ({ value }) => formatDate(value),
      },
    ],
    [formatDate, t],
  );

  const proteoformRecords = proteoformData?.records ?? [];

  return (
    <Stack className={styles.container} fullHeight fullWidth orientation="vertical" gap={8}>
      {/* Back navigation */}
      <Stack className={clsx(pageStyles.titleRow, styles.titleRow)} justifyContent="center">
        <Link className={clsx(getTextClassName('lg'), styles.backLink)} to="/samples">
          {t('samples.details.backToSamples', 'Back to Samples')}
        </Link>
      </Stack>

      {/* Page title */}
      <Text as="h1" size="xl">
        {sample.name}
      </Text>

      <AccordionGroup
        allowsMultipleExpanded
        className={styles.accordionGroup}
        expandedKeys={expandedKeys}
        onExpandedChange={setExpandedKeys}
      >
        {/* Details section */}
        <Accordion
          className={styles.accordionSection}
          classNames={{ panel: styles.accordionPanel }}
          data-testid="details-accordion"
          id="details"
          label={t('samples.details.detailsHeading', 'Details')}
          showChevron
          variant="plain"
        >
          <Stack
            className={styles.detailsCard}
            fullHeight
            fullWidth
            justifyContent="space-between"
            orientation="vertical"
          >
            {/* Organisation */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldOrganisation', 'Organisation')}</Text>
              <Text className={styles.fieldValue}>{sample.org_name}</Text>
            </Stack>

            {/* Study */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldStudy', 'Study')}</Text>
              <Link className={getTextClassName('md')} to="/studies/$studyId" params={{ studyId: sample.study_id }}>
                {sample.study_name}
              </Link>
            </Stack>

            {/* Instrument Run */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldInstrumentRun', 'Instrument Run')}</Text>
              <Link
                className={getTextClassName('md')}
                to="/instrument_runs/$instrumentRunId"
                params={{ instrumentRunId: sample.instrument_run_id }}
              >
                {sample.instrument_run_name}
              </Link>
            </Stack>

            {/* Well Position */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldWellPosition', 'Well Position')}</Text>
              <Text className={styles.fieldValue}>{sample.well_position}</Text>
            </Stack>

            {/* Demux Index */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldDemuxIndex', 'Demux Index')}</Text>
              <Text className={styles.fieldValue}>{sample.demux_index}</Text>
            </Stack>

            {/* Created At */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldCreatedAt', 'Created At')}</Text>
              <Text className={styles.fieldValue}>{formatDate(sample.created_at)}</Text>
            </Stack>

            {/* Updated At */}
            <Stack {...sharedStackProps}>
              <Text className={styles.fieldLabel}>{t('samples.details.fieldUpdatedAt', 'Updated At')}</Text>
              <Text className={styles.fieldValue}>
                {sample.updated_at ? formatDate(sample.updated_at) : t('empty', { ns: 'general' })}
              </Text>
            </Stack>
          </Stack>
        </Accordion>

        {/* Proteoforms section */}
        <Accordion
          className={styles.accordionSection}
          classNames={{ panel: styles.accordionPanel }}
          data-testid="proteoforms-accordion"
          id="proteoforms"
          label={t('proteoforms.title')}
          showChevron
          variant="plain"
        >
          <Stack className={styles.proteoformsSection} fullWidth orientation="vertical" gap={3}>
            {proteoformsLoading && <FullLoading label={t('proteoforms.loading')} />}

            {proteoformsError && <Text>{t('proteoforms.error')}</Text>}

            {!proteoformsLoading && !proteoformsError && proteoformRecords.length === 0 && (
              <Text>{t('proteoforms.noData')}</Text>
            )}

            {!proteoformsLoading && !proteoformsError && proteoformRecords.length > 0 && (
              <Table<SampleProteoformPublic>
                ariaLabel={t('proteoforms.tableAriaLabel')}
                columnDefs={columnDefs}
                density="compact"
                domLayout="autoHeight"
                enableResponsiveResize
                pagination={
                  proteoformData !== null && proteoformData !== undefined && proteoformData.total > proteoformData.limit
                }
                paginationPageSize={proteoformData?.limit}
                rowData={proteoformRecords}
                tableId={`proteoforms-table-${sample.id}`}
                zebraStriping
              />
            )}
          </Stack>
        </Accordion>
      </AccordionGroup>
    </Stack>
  );
}

export const SampleDetailsPage = memo(SampleDetailsPageComponent);
